<template>
  <div class="source-content show-library flex h-full min-h-0 flex-col">
        <div class="p-[0.625rem] pb-0">
          <search-filter
            :value="search"
            searchOnly
            data-test="producer-showLibrary-searchFilter"
            @update:value="search = $event"
          />
        </div>

        <div
          ref="sourceList"
          class="source-list show-library__list min-h-0 flex-1"
          data-test="producer-showLibrary-list"
        >
          <div
            v-if="isLoading && !shows.length"
            class="loading loading--inline mt-4"
            data-test="producer-showLibrary-loading"
          >
            <i class="fas fa-spinner fa-spin"></i>
          </div>

          <div v-else-if="loadError" class="py-8 text-center" data-test="producer-showLibrary-error">
            <p class="mb-2">Failed to load shows.</p>
            <button
              class="tb-btn tb-btn-contained sm medium-em"
              data-test="producer-showLibrary-retry"
              @click="reload"
            >
              Try Again
            </button>
          </div>

          <div v-else-if="!shows.length" class="py-8 text-center" data-test="producer-showLibrary-empty">
            <h3 class="mb-4 text-[14px] font-semibold">No shows found</h3>
          </div>

          <template v-else>
            <!-- Shows -->
            <div
              v-for="show in shows"
              :key="show.id"
              class="show-library__show"
              :data-test="`producer-showLibrary-show:${show.id}`"
            >
              <draggable
                :list="[show]"
                :clone="cloneShow"
                :sort="false"
                animation="200"
                :group="{ name: 'timeline', pull: 'clone', put: false }"
                revert-on-spill="true"
                data-test="producer-showLibrary-showsList"
                @start="startShowDrag"
                @end="onDragEnd"
              >
                <div class="show-library__card show-library__card--show" :data-show-id="show.id">
                  <!-- Header -->
                  <div
                    class="show-library__card-header"
                    :class="{ 'show-library__card-header--collapsed': !expandedShows[show.id] }"
                  >
                    <div class="tb-checkbox show-library__select-all">
                      <input
                        type="checkbox"
                        :id="`show-all-${show.id}`"
                        :checked="isShowAllSelected(show)"
                        :data-test="`producer-showLibrary-show-selectAll:${show.id}`"
                        @change="toggleShowAll(show)"
                      />
                      <label :for="`show-all-${show.id}`" />
                    </div>

                    <button
                      class="show-library__chevron"
                      :aria-expanded="!!expandedShows[show.id]"
                      aria-label="Toggle show"
                      data-test="producer-showLibrary-showToggle"
                      @click.stop="toggleShow(show)"
                    >
                      <span
                        class="fas fa-chevron-right"
                        :class="{ 'show-library__chevron--open': expandedShows[show.id] }"
                      />
                    </button>

                    <div class="show-library__card-info" @click.stop="toggleShow(show)">
                      <span class="show-library__card-title" :title="show.title">{{ show.title }}</span>
                      <span class="show-library__card-meta">{{ showSummary(show) }}</span>
                    </div>

                    <button
                      class="tb-btn tb-btn-contained sm medium-em add-to-playlist show-library__add"
                      title="Add show to timeline"
                      :data-test="`producer-showLibrary-show-add:${show.id}`"
                      @click.stop="$store.dispatch('addShowToTimeline', { showId: show.id })"
                    >
                      <span class="fas fa-plus" />
                    </button>
                  </div>

                  <!-- Contents -->
                  <div
                    v-if="expandedShows[show.id]"
                    class="show-library__card-body"
                    data-test="producer-showLibrary-showBody"
                  >
                    <div
                      v-if="showStates[show.id]?.loading"
                      class="show-library__loading"
                      data-test="producer-showLibrary-showLoading"
                    >
                      <i class="fas fa-spinner fa-spin"></i>
                    </div>

                    <div v-else-if="showStates[show.id]?.error" class="show-library__error">
                      <p class="mb-2">Failed to load this show.</p>
                      <button
                        class="tb-btn tb-btn-contained sm medium-em"
                        data-test="producer-showLibrary-showRetry"
                        @click="loadShow(show)"
                      >
                        Retry
                      </button>
                    </div>

                    <template v-else-if="showItems[show.id]?.length">
                      <draggable
                        :list="showItems[show.id]"
                        :clone="cloneShowChild"
                        :sort="false"
                        animation="200"
                        :group="{ name: 'timeline', pull: 'clone', put: false }"
                        revert-on-spill="true"
                        data-test="producer-showLibrary-itemsList"
                        @start="startShowItemDrag"
                        @end="onDragEnd"
                      >
                        <div
                          v-for="showItem in showItems[show.id]"
                          :key="showItem.id || showItem.itemId"
                          class="show-library__child"
                          :data-show-id="show.id"
                          :data-show-item-id="showItem.itemId"
                          :data-item-type="showItem.itemType"
                          :data-test="`producer-showLibrary-item:${showItem.itemId}`"
                        >
                          <!-- Segment card -->
                          <div
                            v-if="showItem.itemType === 'segment'"
                            class="show-library__card show-library__card--nested"
                          >
                            <div class="show-library__card-row">
                              <div class="tb-checkbox show-library__select-all">
                                <input
                                  type="checkbox"
                                  :id="`seg-all-${show.id}-${showItem.itemId}`"
                                  :checked="isSegmentAllSelected(showItem)"
                                  :data-test="`producer-showLibrary-segment-selectAll:${showItem.itemId}`"
                                  @change="toggleSegmentAll(showItem)"
                                />
                                <label :for="`seg-all-${show.id}-${showItem.itemId}`" />
                              </div>

                              <span class="fak fa-segment show-library__card-icon" />
                              <span
                                class="show-library__card-title"
                                :title="showItem.data.title"
                              >{{ showItem.data.title || 'Untitled segment' }}</span>

                              <button
                                class="tb-btn tb-btn-contained sm medium-em add-to-playlist show-library__add"
                                title="Add segment to timeline"
                                :data-test="`producer-showLibrary-segment-add:${showItem.itemId}`"
                                @click.stop="addSegmentToTimeline(showItem)"
                              >
                                <span class="fas fa-plus" />
                              </button>
                            </div>

                            <!-- Segment contents -->
                            <div class="show-library__card-body">
                              <draggable
                                v-if="(showItem.data.items || []).length"
                                :list="showItem.data.items || []"
                                :clone="cloneNestedItem"
                                :sort="false"
                                animation="200"
                                :group="{ name: 'timeline', pull: 'clone', put: false }"
                                revert-on-spill="true"
                                data-test="producer-showLibrary-segmentItemsList"
                                @start="startNestedItemDrag"
                                @end="onDragEnd"
                              >
                                <show-nested-item
                                  v-for="child in showItem.data.items || []"
                                  :key="child.id"
                                  :item="child"
                                  :show-id="show.id"
                                  :data-show-id="show.id"
                                  :data-show-item-id="child.id"
                                  :data-test="`producer-showLibrary-segmentItem:${child.id}`"
                                />
                              </draggable>
                            </div>
                          </div>

                          <!-- Standalone item / marker / graphic group -->
                          <show-nested-item
                            v-else
                            :item="showItem"
                            :show-id="show.id"
                            :data-show-id="show.id"
                            :data-show-item-id="showItem.itemId"
                          />
                        </div>
                      </draggable>
                    </template>
                  </div>
                </div>
              </draggable>
            </div>
          </template>

          <!-- Infinite scroll trigger -->
          <div ref="loadTrigger" class="load-trigger flex min-h-16 w-full items-center justify-center">
            <div
              v-if="isLoadingMore"
              class="loading loading--inline m-0"
              data-test="producer-showLibrary-loadingMore"
            >
              <i class="fas fa-spinner fa-spin"></i>
            </div>
          </div>
        </div>

        <!-- Selected items -->
        <div
          v-if="selected.length"
          class="producer-assets-selected flex items-center justify-end p-4"
          data-test="producer-showLibrary-selected"
        >
          <p
            class="producer-assets-selected__count mb-0 mr-4 mt-0 text-primary"
            data-test="producer-showLibrary-selected-count"
          >
            {{ selected.length }} selected
          </p>

          <button
            class="tb-btn tb-btn-contained medium-em ml-4"
            data-test="producer-showLibrary-selected-addToPlaylist"
            @click="addSelectedToPlaylist"
          >
            Add to playlist
          </button>
        </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import getResizedImageUrl from '@/js/utils/imageUrl';

import ShowsService from '../../services/shows';
import { getMultiple as getMultipleAssets } from '../../services/assets';
import GraphicsService from '../../services/graphics';
import { getMultipleThemes } from '../../services/themes';
import ShowNestedItem from './ShowNestedItem.vue';
import {
  buildTimelineEntry,
  buildSegmentEntry,
  flattenShowItems,
  getDragTypeForItem,
  collectResourceIds,
  collectAssetIds,
  collectThemeIds,
  NON_THEME_RESOURCE_TYPES,
} from '../../utils/showToTimeline';

/**
 * Collect the ids of every graphic template referenced by a Show's items,
 * so their `preview_url` can be resolved for thumbnails.
 */
const collectTemplateIds = (items) => collectResourceIds(
  items,
  (data) => data?.resourceType === 'graphic' && data.resourceId != null,
);

export default {
  name: 'ShowList',

  components: {
    SearchFilter,
    ShowNestedItem,
  },

  provide() {
    return {
      showLibrary: this,
    };
  },

  data() {
    return {
      shows: [],
      search: '',
      page: 1,
      total: 0,
      limit: 100,
      isLoading: false,
      isLoadingMore: false,
      loadError: false,
      expandedShows: {},
      showStates: {},
      showItems: {},
      showLoaders: {},
      assetDetails: {},
      themeDetails: {},
      templateDetails: {},
      selected: [],
      dragToken: 0,
    };
  },

  computed: {
    ownerId() {
      return this.$store.state.production.production.owner;
    },

    production() {
      return this.$store.state.production.production;
    },

    hasMoreShows() {
      return this.shows.length < this.total;
    },

    dragOptions() {
      return {
        owner: this.ownerId,
        optimizeImages: this.$store.getters.optimizeImages,
        transition: this.production?.transition,
        assets: this.assetDetails,
        themes: this.themeDetails,
      };
    },
  },

  watch: {
    ownerId() {
      this.reload();
    },

    search() {
      // Debounced search against the API (resets pagination to page 1).
      this.searchDebounced();
    },

    shows() {
      // If the list isn't scrollable yet, keep loading pages until it is.
      if (!this.$refs.sourceList || !this.shows.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        this.loadMore();
      }
    },
  },

  created() {
    // Refresh the list after a production is saved as a Show.
    document.addEventListener('showsChanged', this.reload);

    this.searchDebounced = debounce(() => {
      this.fetchShows({ reset: true });
    }, 300);
  },

  mounted() {
    this.reload();

    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  beforeUnmount() {
    document.removeEventListener('showsChanged', this.reload);

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },

  methods: {
    async reload() {
      // Snapshot the expanded shows and any in-flight full-Show loads so the
      // refresh can restore the expansions and refetch their contents.
      // Waiting for in-flight loads first keeps their (possibly stale)
      // results from repopulating the cache after we invalidate it below.
      const previouslyExpanded = Object.keys(this.expandedShows)
        .filter((id) => this.expandedShows[id]);

      await Promise.allSettled(Object.values(this.showLoaders));

      // Invalidate the full Show cache. Refresh is meant to pick up external
      // changes (including nested segments / segment items), so every show
      // must reload its contents from the API on next expand instead of
      // serving the cached hierarchy.
      this.expandedShows = {};
      this.showStates = {};
      this.showItems = {};
      this.showLoaders = {};
      this.selected = [];
      this.assetDetails = {};
      this.themeDetails = {};
      this.templateDetails = {};

      await this.fetchShows({ reset: true });

      // Refetch the full hierarchy for shows that were expanded, so the
      // refresh immediately reflects external changes instead of stale cache.
      // Collapsed shows stay collapsed and refetch on next expand.
      await Promise.allSettled(previouslyExpanded.map(async (id) => {
        const show = this.shows.find((s) => String(s.id) === String(id));

        if (!show) {
          return; // Show no longer exists (deleted or moved to another owner)
        }

        this.expandedShows = {
          ...this.expandedShows,
          [id]: true,
        };

        await this.loadShow(show).catch(() => {});
      }));
    },

    /**
     * Fetch shows from the API, either replacing the list (reset) or
     * appending the next page for infinite scrolling.
     */
    async fetchShows({ reset = false } = {}) {
      if (this.isLoading || this.isLoadingMore) {
        if (reset) {
          // Do not lose the newest search term or refresh request.
          this.pendingReset = true;
        }

        return;
      }

      const nextPage = reset ? 1 : this.page + 1;

      if (reset) {
        this.isLoading = true;
      } else {
        this.isLoadingMore = true;
      }

      this.loadError = false;

      try {
        const result = await ShowsService.getShows({
          page: nextPage,
          limit: this.limit,
          search: this.search.trim(),
        });

        this.shows = reset
          ? (result.shows || [])
          : [...this.shows, ...(result.shows || [])];

        this.total = result.total || 0;
        this.page = nextPage;
      } catch (err) {
        console.error('Failed to load shows:', err);
        this.loadError = true;
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;

        if (this.pendingReset) {
          this.pendingReset = false;
          this.fetchShows({ reset: true });
        }
      }
    },

    loadMore(entries) {
      if (Array.isArray(entries) && !entries[0]?.isIntersecting) {
        return;
      }

      if (this.hasMoreShows) {
        this.fetchShows({ reset: false });
      }
    },

    /**
     * Fetch and cache the full resolved hierarchy of a Show. Always returns
     * a promise resolving to the full Show object so callers can chain.
     */
    loadShow(show) {
      const { id } = show;

      if (this.showStates[id]?.full) {
        return Promise.resolve(this.showStates[id].full);
      }

      if (this.showLoaders[id]) {
        return this.showLoaders[id];
      }

      this.showStates = {
        ...this.showStates,
        [id]: { loading: true, error: null, full: null },
      };

      const promise = ShowsService.getShow(id)
        .then(async (full) => {
          // Media assets are referenced by id only, so fetch their details
          // (media_url, asset_type, ...) up front to embed on flattened items.
          const assetIds = collectAssetIds(full.items || []);

          if (assetIds.length) {
            try {
              const resp = await getMultipleAssets(assetIds);

              const assets = {};
              (resp.assets || []).forEach((asset) => {
                assets[String(asset._id)] = asset;
              });

              this.assetDetails = {
                ...this.assetDetails,
                ...assets,
              };
            } catch (assetErr) {
              console.error(`Failed to load assets for show ${id}:`, assetErr);
            }
          }

          // Themes are also referenced by id only, and their `layout`
          // sub-type (e.g. 'lowerthird', 'grid') lives on the theme record.
          const themeIds = collectThemeIds(full.items || []);

          if (themeIds.length) {
            try {
              const resp = await getMultipleThemes(themeIds);

              const themes = {};
              (resp.themes || []).forEach((theme) => {
                themes[String(theme._id)] = theme;
              });

              this.themeDetails = {
                ...this.themeDetails,
                ...themes,
              };
            } catch (themeErr) {
              console.error(`Failed to load themes for show ${id}:`, themeErr);
            }
          }

          // Graphic thumbnails aren't stored on Shows generated from a
          // production ("Save as Show"), so fetch the template records to
          // resolve their preview_url for the resource cards.
          const templateIds = collectTemplateIds(full.items || []);

          if (templateIds.length) {
            try {
              const templates = await GraphicsService.getMultipleTemplates(templateIds);

              const templateMap = {};
              (templates || []).forEach((template) => {
                templateMap[String(template._id)] = template;
              });

              this.templateDetails = {
                ...this.templateDetails,
                ...templateMap,
              };
            } catch (templateErr) {
              console.error(`Failed to load templates for show ${id}:`, templateErr);
            }
          }

          this.showItems = {
            ...this.showItems,
            [id]: full.items || [],
          };

          this.showStates = {
            ...this.showStates,
            [id]: { loading: false, error: null, full },
          };

          return full;
        })
        .catch((err) => {
          console.error(`Failed to load show ${id}:`, err);

          this.showStates = {
            ...this.showStates,
            [id]: { loading: false, error: err, full: null },
          };

          throw err;
        })
        .finally(() => {
          delete this.showLoaders[id];
        });

      this.showLoaders[id] = promise;

      return promise;
    },

    toggleShow(show) {
      if (this.expandedShows[show.id]) {
        this.expandedShows = {
          ...this.expandedShows,
          [show.id]: false,
        };

        return;
      }

      this.expandedShows = {
        ...this.expandedShows,
        [show.id]: true,
      };

      if (!this.showItems[show.id]) {
        this.loadShow(show).catch(() => {});
      }
    },

    // ------------------------------------------------------------------
    // Selection
    // ------------------------------------------------------------------

    toggleSelected(item) {
      const idx = this.selected.indexOf(item);

      if (idx === -1) {
        this.selected.push(item);
      } else {
        this.selected.splice(idx, 1);
      }
    },

    isItemSelected(item) {
      return this.selected.includes(item);
    },

    /**
     * Collect an item (a segment ShowItem or a bare graphic-group SegmentItem)
     * plus every descendant, so the "All" checkbox can select (and signal)
     * the whole subtree.
     */
    collectEntries(item) {
      const entries = [item];
      const children = item.data ? (item.data.items || []) : (item.items || []);

      const walk = (items) => (items || []).forEach((child) => {
        entries.push(child);
        walk(child.items);
      });

      walk(children);

      return entries;
    },

    collectSegmentEntries(showItem) {
      return this.collectEntries(showItem);
    },

    /**
     * Collect every ShowItem in a show plus all descendants, so the show's
     * "All" checkbox can select (and signal) the entire contents.
     */
    collectShowEntries(show) {
      const entries = [];

      (this.showItems[show.id] || []).forEach((showItem) => {
        entries.push(...this.collectSegmentEntries(showItem));
      });

      return entries;
    },

    isSegmentAllSelected(showItem) {
      const entries = this.collectSegmentEntries(showItem);

      return entries.length > 0 && entries.every((i) => this.selected.includes(i));
    },

    toggleSegmentAll(showItem) {
      this.toggleEntries(this.collectSegmentEntries(showItem));
    },

    isGroupAllSelected(item) {
      const entries = this.collectEntries(item);

      return entries.length > 0 && entries.every((i) => this.selected.includes(i));
    },

    toggleGroupAll(item) {
      this.toggleEntries(this.collectEntries(item));
    },

    async addGroupToTimeline(item) {
      await this.addLayoutsToTimeline([buildTimelineEntry(item, this.dragOptions)]);
    },

    isShowAllSelected(show) {
      const entries = this.collectShowEntries(show);

      return entries.length > 0 && entries.every((i) => this.selected.includes(i));
    },

    async toggleShowAll(show) {
      // The Show's contents aren't known until it's been expanded/loaded, so
      // load it first when checking the box on a collapsed Show. This way the
      // selection (and the "Add to playlist" CTA) work without expanding.
      if (!this.showItems[show.id]) {
        await this.loadShow(show).catch(() => {});
      }

      this.toggleEntries(this.collectShowEntries(show));
    },

    toggleEntries(entries) {
      const allSelected = entries.every((i) => this.selected.includes(i));

      if (allSelected) {
        this.selected = this.selected.filter((i) => !entries.includes(i));
        return;
      }

      const next = [...this.selected];
      entries.forEach((i) => {
        if (!next.includes(i)) {
          next.push(i);
        }
      });

      this.selected = next;
    },

    // ------------------------------------------------------------------
    // Drag + drop
    // ------------------------------------------------------------------

    startShowDrag(e) {
      const { showId } = e.item.dataset;
      const show = this.shows.find((s) => String(s.id) === String(showId));

      if (!show) {
        return;
      }

      this.dragToken += 1;
      const token = this.dragToken;

      this.$store.dispatch('setDragType', { type: 'segment' });

      // If the full hierarchy is already loaded (show was expanded), we can
      // flatten it synchronously. Otherwise fetch it on the fly and hand the
      // load to the drag store as a pending group, so a drop that lands
      // before the fetch resolves still inserts the real contents instead of
      // the placeholder segment.
      const loaded = this.showItems[show.id];
      if (loaded) {
        this.$store.dispatch('setDragType', {
          type: 'segment',
          group: flattenShowItems(loaded, this.dragOptions),
        });

        return;
      }

      const pending = this.loadShow(show)
        .then((full) => flattenShowItems(full.items || [], this.dragOptions))
        .catch(() => null);

      this.$store.dispatch('setDragType', {
        type: 'segment',
        pendingGroup: pending,
      });

      // If the contents finish loading before the drop lands, populate the
      // drag group so the timeline can add them without awaiting. Drops that
      // land first will await the pending group instead.
      pending.then((items) => {
        if (items && token === this.dragToken) {
          this.$store.dispatch('setDragType', {
            type: 'segment',
            group: items,
          });
        }
      });
    },

    startShowItemDrag(e) {
      const { showId, showItemId } = e.item.dataset;
      const item = this.findShowItem(showId, showItemId);

      if (!item) {
        return;
      }

      if (item.itemType === 'segment') {
        this.$store.dispatch('setDragType', {
          type: 'segment',
          group: [buildSegmentEntry(item.data, this.dragOptions)],
        });

        return;
      }

      this.$store.dispatch('setDragType', {
        type: getDragTypeForItem(item),
      });
    },

    startNestedItemDrag(e) {
      const { showId, showItemId } = e.item.dataset;

      if (showId == null || showItemId == null) {
        return;
      }

      const child = this.findNestedChild(showId, showItemId);

      if (child) {
        this.$store.dispatch('setDragType', {
          type: getDragTypeForItem(child),
        });
      }
    },

    onDragEnd() {
      this.dragToken += 1;
      this.$store.dispatch('clearDragType');
    },

    // ------------------------------------------------------------------
    // Clone functions (vuedraggable pull: 'clone')
    // ------------------------------------------------------------------

    cloneShow(show) {
      // Placeholder for a whole-Show drop. When the flattened items have been
      // attached to the drag group, the timeline replaces this with those
      // items. Stands alone as an empty segment otherwise.
      return {
        title: show.title,
        timeline_id: crypto.randomUUID(),
        displayTime: 5000,
        transition: this.production?.transition || 'Cut',
        layout: 'segment',
        notes: '',
        settings: {},
        posts: [],
        fields: {},
        meta: {},
        owner: this.ownerId,
      };
    },

    cloneShowChild(showItem) {
      if (showItem.itemType === 'segment') {
        return buildSegmentEntry(showItem.data, this.dragOptions);
      }

      return buildTimelineEntry(showItem.data, this.dragOptions);
    },

    cloneNestedItem(child) {
      return buildTimelineEntry(child, this.dragOptions);
    },

    // ------------------------------------------------------------------
    // Add to timeline
    // ------------------------------------------------------------------

    async addLayoutsToTimeline(items) {
      if (!items.length) {
        return;
      }

      if (!this.$store.state.clients.isSocketConnected) {
        console.warn('Ignoring add to timeline while disconnected from socket server');
        return;
      }

      await this.$store.dispatch('addLayouts', { items });
      await this.$store.dispatch('getTimelineItems');
      this.$store.dispatch('updateTimeline');
      this.$store.dispatch('trackSportradarInGameElement');
    },

    async addSegmentToTimeline(showItem) {
      await this.addLayoutsToTimeline([buildSegmentEntry(showItem.data, this.dragOptions)]);
    },

    async addSelectedToPlaylist() {
      await this.addLayoutsToTimeline(this.flattenSelected());

      this.selected = [];
    },

    /**
     * Flatten the selected entries into timeline items. Descendants that are
     * already covered by a selected segment or graphic group are skipped, so
     * selecting a segment with "All" doesn't add its children twice.
     */
    flattenSelected() {
      const covered = new Set();

      const markCovered = (item) => {
        const children = item.itemType ? (item.data?.items || []) : (item.items || []);

        children.forEach((child) => {
          covered.add(child);
          markCovered(child);
        });
      };

      this.selected.forEach(markCovered);

      return this.selected
        .filter((item) => !covered.has(item))
        .map((item) => this.flattenEntry(item));
    },

    flattenEntry(item) {
      if (item.itemType === 'segment') {
        return buildSegmentEntry(item.data, this.dragOptions);
      }

      return buildTimelineEntry(item.data || item, this.dragOptions);
    },

    // ------------------------------------------------------------------
    // Lookups
    // ------------------------------------------------------------------

    findShowItem(showId, showItemId) {
      const items = this.showItems[showId] || [];

      return items.find((i) => String(i.itemId) === String(showItemId));
    },

    findNestedChild(showId, itemId) {
      const items = this.showItems[showId] || [];
      const candidates = items.flatMap((showItem) => {
        if (showItem.itemType === 'segment') {
          return showItem.data.items || [];
        }

        if (showItem.itemType === 'segment-item' && showItem.data.items?.length) {
          return showItem.data.items;
        }

        return [];
      });

      return this.findInTree(candidates, itemId);
    },

    findInTree(items, itemId) {
      const list = items || [];
      const direct = list.find((child) => String(child.id) === String(itemId));

      if (direct) {
        return direct;
      }

      return list.reduce((found, child) => (
        found || this.findInTree(child.items, itemId)
      ), undefined);
    },

    // ------------------------------------------------------------------
    // Rendering helpers
    // ------------------------------------------------------------------

    showSummary(show) {
      // Prefer the metadata from the list endpoint so the header shows the
      // counts before the Show has ever been expanded.
      const segments = show.segmentCount ?? null;
      const displays = show.displayCount ?? null;

      if (segments != null && displays != null) {
        return `${segments} segment${segments === 1 ? '' : 's'} • ${displays} display${displays === 1 ? '' : 's'}`;
      }

      // Fallback for shows fetched before the metadata was added
      const items = this.showItems[show.id] || [];

      if (!items.length) {
        return '';
      }

      const segs = items.filter((i) => i.itemType === 'segment').length;
      const disp = this.countDisplays(items);

      return `${segs} segment${segs === 1 ? '' : 's'} • ${disp} display${disp === 1 ? '' : 's'}`;
    },

    countDisplays(items) {
      let count = 0;

      const walk = (list) => (list || []).forEach((item) => {
        const data = item.data || item;

        if (data?.resourceType && data.resourceType !== 'marker') {
          count += 1;
        }

        walk(data?.items);
      });

      walk(items);

      return count;
    },

    itemPreviewUrl(item) {
      const data = item.data || item;
      const { resourceType, resourceId } = data;

      if (resourceType === 'asset') {
        const asset = this.assetDetails[String(resourceId)];

        return getResizedImageUrl(
          asset?.thumbnail_url || asset?.media_url,
          'w',
          50,
        ) || null;
      }

      if (resourceType === 'graphic') {
        // Shows generated from a production don't store the graphic's
        // preview, so resolve it from the fetched template record.
        return this.templateDetails[String(resourceId)]?.preview_url || data.previewUrl || null;
      }

      if (resourceType && !NON_THEME_RESOURCE_TYPES.has(resourceType)) {
        return this.themeDetails[String(resourceId)]?.preview_url || data.previewUrl || null;
      }

      return data.previewUrl || null;
    },

    itemTypeLabel(item) {
      const data = item.data || item;
      const { resourceType } = data;

      if (resourceType === 'asset') {
        return 'media';
      }

      if (resourceType && !NON_THEME_RESOURCE_TYPES.has(resourceType)) {
        // Themes come in many layouts (lowerthird, grid, ...) but are all
        // labeled "social", matching the GlobalSearch tab.
        return 'social';
      }

      return resourceType || 'item';
    },

    markerStyle(item) {
      const data = item.data || item;
      const color = data.fields?.color || data.color || '#CC9F10';

      return { '--marker-color': color };
    },
  },
};
</script>

<style lang="scss" scoped>
.show-library {
  &__list {
    // The toolbar (and its padding) was removed; keep a little breathing
    // room above the first Show card.
    padding-top: 0.625rem;
  }

  &__show {
    margin-bottom: 0.75rem;
  }

  &__card {
    display: flex;
    padding: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
    align-self: stretch;
    border-radius: 6px;
    border: 1px solid rgba(66, 66, 66, 0.5);
    background: #262626;
    cursor: grab;
  }

  &__card--show {
    padding: 0;
    gap: 0;

    // The header spans the full top edge of the card; the contents keep
    // their own padding.
    .show-library__card-body {
      padding: 0.5rem;
    }
  }

  &__card-header {
    display: flex;
    padding: 5px 10px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius: 6px 6px 0 0;
    border-bottom: 1px solid #424242;
    background: rgba(255, 255, 255, 0.05);
    gap: 0.5rem;
    min-width: 0;

    &--collapsed {
      border-bottom: none;
      border-radius: 6px;
    }
  }

  &__card-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    align-self: stretch;
    min-width: 0;

    .show-library__card-title {
      flex: 1 1 auto;
    }
  }

  &__card-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__card-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #fff;
    font-weight: 500;
    font-size: 0.75rem;
  }

  &__card-meta {
    color: #a5a5a5;
    font-size: 0.6875rem;
  }

  &__card-icon {
    flex-shrink: 0;
    color: #a5a5a5;
    font-size: 0.8125rem;
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    align-self: stretch;
    min-width: 0;
  }

  &__chevron {
    flex-shrink: 0;
    border: 0;
    background: transparent;
    color: #a5a5a5;
    padding: 0;
    font-size: 0.75rem;
    cursor: pointer;
    line-height: 1;

    &:hover {
      color: #fff;
    }

    span {
      display: block;
      transition: transform 0.15s ease;
    }
  }

  &__chevron--open {
    transform: rotate(90deg);
  }

  &__select-all {
    flex-shrink: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;

    // Must match the specificity of the global `.tb-checkbox input + label`
    // rule or the default padding/gutters win the cascade. The label is sized
    // to the box so the flex gap separates it from the next header element.
    input[type='checkbox'] + label {
      width: 1.5em;
      min-height: 1.5em;
      line-height: 1;
      padding-left: 0;
      font-size: 0.6875rem;
    }
  }

  &__add {
    flex-shrink: 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 0.75rem 0;
    color: #a5a5a5;

    i {
      font-size: 1rem;
    }
  }

  &__error {
    padding: 0.75rem;
    color: #a5a5a5;
    text-align: center;
  }
}
</style>
